import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { ContextItemSheet } from "./ContextItemSheet";

import { type ContextItemView, type VisitContext } from "@/lib/api";
import { formatShortDate } from "@/lib/context-checks";
import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";

interface Props {
    visitId: string | number;
    context: VisitContext | null;
}

const EVENT_LABEL: Record<string, string> = {
    fall: "Fall",
    hospitalisation: "Hospital",
    hospitalization: "Hospital",
    discharge: "Discharge",
    wound: "Wound",
    visit: "Visit",
    procedure: "Procedure",
    other: "Event"
};

/** "2026-08-12" → "Aug 12, 2026"; "2026-08" → "Aug 2026"; "2026" → "2026". */
function formatEventDate(eventAt: string, precision: "day" | "month" | "year"): string {
    const [y, m, d] = eventAt.split("-").map(Number);
    if (precision === "year" || !m) {
        return String(y);
    }
    const month = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" }).format(
        new Date(Date.UTC(y, m - 1, 1))
    );
    if (precision === "month" || !d) {
        return `${month} ${y}`;
    }
    return `${month} ${d}, ${y}`;
}

/**
 * "Before you go in" — the intro card (the latest lines + a chip per source)
 * and the timeline card beneath it. Always current: the hints are rebuilt as
 * the patient's context changes, before and after clock-in alike, so there is
 * nothing to "bring in" — new context simply appears.
 *
 * States are drawn distinctly and none of them is a green tick: a rebuild on
 * its way shows a spinner line ("Updating…") over the hints she already has,
 * `failed` says so and keeps the last good hints, and a visit with no sources
 * at all renders nothing.
 */
export function PatientContextCard({ visitId, context }: Props) {
    const [sheet, setSheet] = useState<{ title: string; items: ContextItemView[] } | null>(null);

    if (!context) {
        return null;
    }
    const allItems = context.sections.flatMap((sec) => sec.items);
    const hasAnything = context.sources.length > 0 || context.status !== "none";
    if (!hasAnything) {
        return null;
    }

    const updating =
        context.status === "pending" || context.status === "processing" || context.stale;
    const hasHints = context.hints.length > 0;
    const introLines = (context.intro ?? "")
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l !== "");

    const openSource = (sourceId: number, label: string) => {
        const items = allItems.filter(
            (i) =>
                i.sourceId === sourceId &&
                i.canOpenSource &&
                ["document", "photo"].includes(i.sourceType)
        );
        if (items.length > 0) {
            setSheet({ title: label, items });
        }
    };
    const openItems = (itemIds: number[], label: string) => {
        const items = allItems.filter(
            (i) =>
                itemIds.includes(i.id) &&
                i.canOpenSource &&
                ["document", "photo"].includes(i.sourceType)
        );
        if (items.length > 0) {
            setSheet({ title: label, items });
        }
    };

    return (
        <View>
            <View style={styles.card}>
                <Text style={styles.sectionLabel}>BEFORE YOU GO IN</Text>

                {updating ? (
                    <View style={styles.statusRow} accessibilityLiveRegion="polite">
                        <ActivityIndicator size="small" color={colors.warning500} />
                        <Text style={styles.statusPreparing}>
                            {hasHints
                                ? "Updating with new information — the hints in the sections will refresh in a moment."
                                : "Preparing the patient\u2019s context — hints will appear in the sections in a moment."}
                        </Text>
                    </View>
                ) : null}
                {context.status === "failed" ? (
                    <View style={styles.statusRow}>
                        <MaterialCommunityIcons
                            name="alert-circle-outline"
                            size={16}
                            color={colors.error500}
                        />
                        <Text style={styles.statusFailed}>
                            {hasHints
                                ? "The latest update failed — showing the hints from before."
                                : "The context could not be prepared. The sections work without hints."}
                        </Text>
                    </View>
                ) : null}

                {introLines.length > 0 ? (
                    introLines.map((line, i) => (
                        <Text key={i} style={[styles.line, i === 0 && styles.headline]}>
                            {line}
                        </Text>
                    ))
                ) : (
                    <Text style={styles.empty}>
                        {context.sources.length > 0
                            ? "No summary yet — open a source below."
                            : "Nothing is known about this patient yet."}
                    </Text>
                )}

                {context.sources.length > 0 ? (
                    <View style={styles.chips}>
                        {context.sources.map((src) => (
                            <Pressable
                                key={src.id}
                                style={styles.chip}
                                onPress={() => openSource(src.id, src.label)}
                                disabled={
                                    !allItems.some(
                                        (i) =>
                                            i.sourceId === src.id &&
                                            i.canOpenSource &&
                                            ["document", "photo"].includes(i.sourceType)
                                    )
                                }
                                accessibilityRole={
                                    ["document", "photo"].includes(src.type) ? "button" : undefined
                                }
                            >
                                <View style={styles.chipDot} />
                                <Text style={styles.chipText}>
                                    {src.label}
                                    {src.type !== "previous_visit" &&
                                    formatShortDate(src.observedAt)
                                        ? ` · ${formatShortDate(src.observedAt)}`
                                        : ""}
                                </Text>
                            </Pressable>
                        ))}
                    </View>
                ) : null}
            </View>

            {context.timeline.length > 0 ? (
                <View style={styles.card}>
                    <Text style={styles.sectionLabel}>TIMELINE</Text>
                    {context.timeline.map((entry, i) => (
                        <Pressable
                            key={`${entry.eventAt}-${i}`}
                            style={[styles.timelineRow, i > 0 && styles.timelineRowBorder]}
                            onPress={() => openItems(entry.itemIds, entry.sourceLabel)}
                            disabled={
                                !allItems.some(
                                    (i) =>
                                        entry.itemIds.includes(i.id) &&
                                        i.canOpenSource &&
                                        ["document", "photo"].includes(i.sourceType)
                                )
                            }
                            accessibilityRole={
                                allItems.some(
                                    (i) =>
                                        entry.itemIds.includes(i.id) &&
                                        i.canOpenSource &&
                                        ["document", "photo"].includes(i.sourceType)
                                )
                                    ? "button"
                                    : undefined
                            }
                        >
                            <View style={styles.timelineDate}>
                                <Text style={styles.timelineDateText}>
                                    {formatEventDate(entry.eventAt, entry.precision)}
                                </Text>
                                <Text style={styles.timelineType}>
                                    {EVENT_LABEL[entry.eventType] ?? entry.eventType}
                                </Text>
                            </View>
                            <View style={styles.timelineBody}>
                                <Text style={styles.timelineText}>{entry.text}</Text>
                                <Text style={styles.timelineSource}>{entry.sourceLabel}</Text>
                            </View>
                        </Pressable>
                    ))}
                </View>
            ) : null}

            <ContextItemSheet
                visitId={visitId}
                items={sheet?.items ?? null}
                title={sheet?.title ?? null}
                onClose={() => setSheet(null)}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        marginHorizontal: spacing.base,
        marginTop: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        padding: spacing.base,
        ...shadows.sm
    },
    sectionLabel: {
        fontFamily: fonts.bold,
        fontSize: 10,
        // teal600, not 500: teal text on white needs it for WCAG AA (CLAUDE.md).
        color: colors.teal600,
        letterSpacing: 1,
        textTransform: "uppercase",
        marginBottom: spacing.md
    },
    statusRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        marginBottom: spacing.md,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.md,
        borderRadius: radius.sm,
        backgroundColor: colors.warnBg
    },
    statusPreparing: {
        flex: 1,
        fontFamily: fonts.medium,
        fontSize: 12,
        lineHeight: 17,
        color: colors.warning600
    },
    statusFailed: {
        flex: 1,
        fontFamily: fonts.medium,
        fontSize: 12,
        lineHeight: 17,
        color: colors.error600
    },
    line: {
        fontFamily: fonts.regular,
        fontSize: 13,
        lineHeight: 20,
        color: colors.textSecondary
    },
    headline: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.textPrimary,
        marginBottom: spacing.xs
    },
    empty: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textTertiary,
        fontStyle: "italic"
    },
    chips: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: spacing.sm,
        marginTop: spacing.md
    },
    chip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        paddingVertical: 5,
        paddingHorizontal: 10,
        borderRadius: radius.full,
        backgroundColor: colors.teal50,
        borderWidth: 1,
        borderColor: colors.teal200
    },
    chipDot: {
        width: 4,
        height: 4,
        borderRadius: 2,
        backgroundColor: colors.teal400
    },
    chipText: {
        fontFamily: fonts.bold,
        fontSize: 11,
        color: colors.teal600,
        letterSpacing: 0.2
    },
    timelineRow: {
        flexDirection: "row",
        gap: spacing.md,
        paddingVertical: spacing.sm
    },
    timelineRowBorder: {
        borderTopWidth: 1,
        borderTopColor: colors.border
    },
    timelineDate: {
        width: 84
    },
    timelineDateText: {
        fontFamily: fonts.bold,
        fontSize: 12,
        color: colors.textPrimary
    },
    timelineType: {
        fontFamily: fonts.medium,
        fontSize: 10,
        color: colors.textTertiary,
        letterSpacing: 0.5,
        textTransform: "uppercase"
    },
    timelineBody: {
        flex: 1
    },
    timelineText: {
        fontFamily: fonts.regular,
        fontSize: 13,
        lineHeight: 19,
        color: colors.textSecondary
    },
    timelineSource: {
        marginTop: 2,
        fontFamily: fonts.medium,
        fontSize: 11,
        color: colors.teal600
    }
});
